// TelcoTrack — Úřady: mustry odchozích zpráv (koncept k potvrzení, ne auto-send).
// Compose Mustr = discovery katalog (orgány + sítě) + 8 povinností + Jiné.

import {
  AUTHORITY_FILING_PACKAGE_KEYS,
  AUTHORITY_FILING_PACKAGE_LABELS_CS,
  AUTHORITY_CONSTRUCTION_PREP_KEYS,
  isAuthorityPovinnostKey,
  type AuthorityFilingPackageKey,
} from './authority-domain.constants';
import { authorityDocumentTypeLabel } from './authority.constants';
import {
  AUTHORITY_DISCOVERY_CATALOG,
  discoveryEntry,
  NETWORK_STAKEOUT_DOCUMENT_TYPE,
} from './authority-urady-categories.constants';
import { PROJECT_DRIVE_SKELETON, type ProjectDriveArtifactFolderKey } from './project-drive-folder.constants';

export const AUTHORITY_LETTER_CHANNELS = ['isds', 'email', 'portal'] as const;
export type AuthorityLetterChannel = (typeof AUTHORITY_LETTER_CHANNELS)[number];

/** Compose-only mustr: user writes subject/body; no proposed attachments. */
export const AUTHORITY_COMPOSE_OTHER_DOCUMENT_TYPE = 'jine';

/** Closing line under „S pozdravem“ — company as sender, not a personal informal sign-off. */
export const AUTHORITY_LETTER_SENDER_LINE = 'za ProTelco s.r.o.';

export function isAuthorityComposeOtherType(value: string | null | undefined): boolean {
  return (value ?? '').trim() === AUTHORITY_COMPOSE_OTHER_DOCUMENT_TYPE;
}

export type AuthorityLetterTemplateVars = {
  stavba: string;
  kia: string;
  sap: string;
  obec: string;
  contact: string;
  documentLabel: string;
  /** Name + KIA/SAP/obec — empty codes omitted (never „KIA —“). */
  stavbaIdent: string;
};

export type AuthorityLetterTemplate = {
  id: string;
  channel: AuthorityLetterChannel | '*';
  documentType: string | '*';
  labelCs: string;
  subjectTpl: string;
  bodyTpl: string;
};

const PREP_COMPOSE_LABEL_CS: Record<string, string> = {
  dio: 'Žádost o DIO',
  aggregates: 'Poptávka dodavatele materiálu',
  container_hauler: 'Poptávka dopravce',
  mobile_wc: 'Poptávka mobilního WC',
  handover_to_site_lead: 'Předání staveniště',
  recycling_site: 'Poptávka skládky',
  material_storage: 'Poptávka deponie',
  stakeout: 'Žádost o vytyčení',
};

function letterClose(): string[] {
  return ['S pozdravem', '', AUTHORITY_LETTER_SENDER_LINE];
}

function letterOpen(channel: 'isds' | 'email'): string[] {
  return channel === 'email' ? ['Dobrý den,', ''] : ['Vážení,', ''];
}

function joinLetter(lines: string[]): string {
  return lines.join('\n');
}

/**
 * Document types offered in Pošta compose Mustr.
 * Derived from importable Dotčené orgány + Křížení sítí + fixed 8 Povinnosti.
 */
export function listAuthorityComposeDocumentTypes(): string[] {
  return [
    ...AUTHORITY_DISCOVERY_CATALOG.map((row) => row.subjectKey),
    ...AUTHORITY_CONSTRUCTION_PREP_KEYS,
  ];
}

/** Identify the stavba in letters: název, KIA, SAP, obec — skip missing parts. */
export function formatAuthorityStavbaIdent(input: {
  stavba?: string | null;
  kia?: string | null;
  sap?: string | null;
  obec?: string | null;
}): string {
  const name = meaningful(input.stavba);
  const parts: string[] = [];
  const kia = meaningful(input.kia);
  const sap = meaningful(input.sap);
  const obec = meaningful(input.obec);
  if (kia) parts.push(`KIA ${kia}`);
  if (sap) parts.push(`SAP ${sap}`);
  if (obec) parts.push(`obec ${obec}`);
  if (name && parts.length) return `${name} (${parts.join(', ')})`;
  if (name) return name;
  if (parts.length) return parts.join(', ');
  return '—';
}

export const AUTHORITY_LETTER_TEMPLATES: readonly AuthorityLetterTemplate[] = [
  {
    id: 'vyjadreni_doss.isds',
    channel: 'isds',
    documentType: 'vyjadreni_doss',
    labelCs: 'Žádost o vyjádření DOSS (datovka)',
    subjectTpl: 'Žádost o vyjádření — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vyjádření k záměru výstavby sítě elektronických komunikací.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'vyjadreni_doss.email',
    channel: 'email',
    documentType: 'vyjadreni_doss',
    labelCs: 'Žádost o vyjádření DOSS (e-mail)',
    subjectTpl: 'Žádost o vyjádření — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vyjádření k záměru výstavby sítě elektronických komunikací.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'archeologicky_ustav.isds',
    channel: 'isds',
    documentType: 'archeologicky_ustav',
    labelCs: 'Oznámení — Archeologický ústav (datovka)',
    subjectTpl: 'Oznámení o záměru na území s archeologickými nálezy — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'oznamujeme Vám záměr stavby sítě elektronických komunikací {{stavbaIdent}} na území s archeologickými nálezy.',
      '',
      'Žádáme o sdělení podmínek archeologického dohledu, případně o uzavření dohody o provedení záchranného archeologického výzkumu.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'archeologicky_ustav.email',
    channel: 'email',
    documentType: 'archeologicky_ustav',
    labelCs: 'Oznámení — Archeologický ústav (e-mail)',
    subjectTpl: 'Oznámení o záměru na území s archeologickými nálezy — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'oznamujeme Vám záměr stavby sítě elektronických komunikací {{stavbaIdent}} na území s archeologickými nálezy.',
      '',
      'Žádáme o sdělení podmínek archeologického dohledu, případně o uzavření dohody o provedení záchranného archeologického výzkumu.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'vyjadreni_site.isds',
    channel: 'isds',
    documentType: 'vyjadreni_site',
    labelCs: 'Žádost o vyjádření k síti (datovka)',
    subjectTpl: 'Žádost o vyjádření k síti — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vyjádření k existenci sítě a k případnému křížení nebo souběhu ({{documentLabel}}).',
      '',
      'Vyjádření o existenci sítě nenahrazuje vyjádření k projektové dokumentaci, je-li síť záměrem dotčena.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'vyjadreni_site.email',
    channel: 'email',
    documentType: 'vyjadreni_site',
    labelCs: 'Žádost o vyjádření k síti (e-mail)',
    subjectTpl: 'Žádost o vyjádření k síti — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vyjádření k existenci sítě a k případnému křížení nebo souběhu ({{documentLabel}}).',
      '',
      'Vyjádření o existenci sítě nenahrazuje vyjádření k projektové dokumentaci, je-li síť záměrem dotčena.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'vyjadreni_site.portal',
    channel: 'portal',
    documentType: 'vyjadreni_site',
    labelCs: 'Žádost o vyjádření k síti (oficiální portál)',
    subjectTpl: 'Podání na portál — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      'Žádost o vyjádření k existenci sítě a k případnému křížení nebo souběhu.',
      '',
      'Stavba: {{stavbaIdent}}',
      'Žadatel: ProTelco s.r.o.',
      'Kontakt: {{contact}}',
      'Síť: {{documentLabel}}',
      '',
      'Zákres zájmového území a přílohy jsou součástí podání na portálu provozovatele.',
    ]),
  },
  {
    id: 'network_stakeout.portal',
    channel: 'portal',
    documentType: NETWORK_STAKEOUT_DOCUMENT_TYPE,
    labelCs: 'Žádost o vytyčení sítě (oficiální portál)',
    subjectTpl: 'Podání na portál — vytyčení sítě — {{stavba}}',
    bodyTpl: joinLetter([
      'Žádost o vytyčení stávající sítě v trase stavby.',
      '',
      'Stavba: {{stavbaIdent}}',
      'Žadatel: ProTelco s.r.o.',
      'Kontakt: {{contact}}',
      'Síť: {{documentLabel}}',
      '',
      'Zákres zájmového území a přílohy jsou součástí podání na portálu provozovatele.',
    ]),
  },
  {
    id: 'network_stakeout.email',
    channel: 'email',
    documentType: NETWORK_STAKEOUT_DOCUMENT_TYPE,
    labelCs: 'Žádost o vytyčení sítě (e-mail)',
    subjectTpl: 'Žádost o vytyčení sítě — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vytyčení stávající sítě ({{documentLabel}}) v trase záměru.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'network_stakeout.isds',
    channel: 'isds',
    documentType: NETWORK_STAKEOUT_DOCUMENT_TYPE,
    labelCs: 'Žádost o vytyčení sítě (datovka)',
    subjectTpl: 'Žádost o vytyčení sítě — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o vytyčení stávající sítě ({{documentLabel}}) v trase záměru.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'stanovisko_hzs.*',
    channel: '*',
    documentType: 'stanovisko_hzs',
    labelCs: 'Žádost o stanovisko HZS',
    subjectTpl: 'Žádost o stanovisko HZS — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o stanovisko Hasičského záchranného sboru k záměru.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'stanovisko_hzs.email',
    channel: 'email',
    documentType: 'stanovisko_hzs',
    labelCs: 'Žádost o stanovisko HZS (e-mail)',
    subjectTpl: 'Žádost o stanovisko HZS — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o stanovisko Hasičského záchranného sboru k záměru.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'dio.*',
    channel: '*',
    documentType: 'dio',
    labelCs: 'Žádost o DIO',
    subjectTpl: 'Žádost o DIO — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o stanovení přechodné úpravy provozu (DIO).',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'dio.email',
    channel: 'email',
    documentType: 'dio',
    labelCs: 'Žádost o DIO (e-mail)',
    subjectTpl: 'Žádost o DIO — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o stanovení přechodné úpravy provozu (DIO).',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'prep.email',
    channel: 'email',
    documentType: 'prep',
    labelCs: 'Poptávka / žádost (příprava)',
    subjectTpl: '{{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}}.',
      '',
      'Předmět: {{documentLabel}}.',
      '',
      'Podklady zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'prep.isds',
    channel: 'isds',
    documentType: 'prep',
    labelCs: 'Poptávka / žádost (příprava, datovka)',
    subjectTpl: '{{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}}.',
      '',
      'Předmět: {{documentLabel}}.',
      '',
      'Podklady zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'generic.isds',
    channel: 'isds',
    documentType: '*',
    labelCs: 'Obecná žádost (datovka)',
    subjectTpl: 'Žádost — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('isds'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o {{documentLabel}}.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
  {
    id: 'generic.email',
    channel: 'email',
    documentType: '*',
    labelCs: 'Obecná žádost (e-mail)',
    subjectTpl: 'Žádost — {{documentLabel}} — {{stavba}}',
    bodyTpl: joinLetter([
      ...letterOpen('email'),
      'obracíme se na Vás ve věci stavby {{stavbaIdent}} se žádostí o {{documentLabel}}.',
      '',
      'Podklady k posouzení zasíláme v příloze.',
      '',
      ...letterClose(),
    ]),
  },
] as const;

const PLACEHOLDER_RE = /\{\{\s*(stavba|kia|sap|obec|contact|documentLabel|stavbaIdent)\s*\}\}/g;

export function renderAuthorityLetterTemplate(
  tpl: string,
  vars: Partial<AuthorityLetterTemplateVars> | null | undefined,
): string {
  const bag = completeLetterVars(vars);
  return tpl.replace(PLACEHOLDER_RE, (_m, key: keyof AuthorityLetterTemplateVars) => bag[key]);
}

/** Resolve letter body template for a compose / obligation document type. */
export function pickAuthorityLetterTemplate(
  documentType: string | null | undefined,
  channel: AuthorityLetterChannel | null | undefined,
): AuthorityLetterTemplate {
  const doc = (documentType ?? '').trim();
  const ch: AuthorityLetterChannel =
    channel === 'email' ? 'email' : channel === 'portal' ? 'portal' : 'isds';
  const exact =
    AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === doc && row.channel === ch) ||
    AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === doc && row.channel === '*');
  if (exact) return exact;

  if (doc === 'archeologicky_ustav') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find(
        (row) => row.documentType === 'archeologicky_ustav' && row.channel === ch,
      ) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'archeologicky_ustav') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }

  if (doc === NETWORK_STAKEOUT_DOCUMENT_TYPE) {
    return (
      AUTHORITY_LETTER_TEMPLATES.find(
        (row) => row.documentType === NETWORK_STAKEOUT_DOCUMENT_TYPE && row.channel === ch,
      ) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === NETWORK_STAKEOUT_DOCUMENT_TYPE) ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }

  const entry = discoveryEntry(doc);
  if (entry?.subjectKey === 'archeologicky_ustav') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find(
        (row) => row.documentType === 'archeologicky_ustav' && row.channel === ch,
      ) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'archeologicky_ustav') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }
  if (entry?.category === 'krizeni_siti') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'vyjadreni_site' && row.channel === ch) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'vyjadreni_site' && row.channel === '*') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }
  if (entry?.documentType === 'stanovisko_hzs' || entry?.subjectKey === 'hzs') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'stanovisko_hzs') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }
  if (entry?.category === 'dotcene_organy') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'vyjadreni_doss' && row.channel === ch) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'vyjadreni_doss' && row.channel === '*') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }

  if (isAuthorityPovinnostKey(doc) && doc !== 'dio') {
    return (
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'prep' && row.channel === ch) ||
      AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === 'prep') ||
      AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
    );
  }

  return (
    AUTHORITY_LETTER_TEMPLATES.find((row) => row.documentType === '*' && row.channel === ch) ||
    AUTHORITY_LETTER_TEMPLATES[AUTHORITY_LETTER_TEMPLATES.length - 1]
  );
}

export function composeMustrLabelCs(
  documentType: string,
  channel: AuthorityLetterChannel | null | undefined,
): string {
  const doc = (documentType || '').trim();
  if (doc === NETWORK_STAKEOUT_DOCUMENT_TYPE) return 'Žádost o vytyčení sítě';
  if (PREP_COMPOSE_LABEL_CS[doc]) return PREP_COMPOSE_LABEL_CS[doc];
  const entry = discoveryEntry(doc);
  if (entry?.subjectKey === 'archeologicky_ustav') {
    return 'Oznámení — Archeologický ústav';
  }
  if (entry?.category === 'dotcene_organy') {
    if (entry.documentType === 'stanovisko_hzs' || entry.subjectKey === 'hzs') {
      return 'Žádost o stanovisko HZS';
    }
    return `Žádost o vyjádření — ${entry.labelCs}`;
  }
  if (entry?.category === 'krizeni_siti') {
    return `Žádost o vyjádření k síti — ${entry.labelCs}`;
  }
  if (isAuthorityPovinnostKey(doc)) {
    const tpl = pickAuthorityLetterTemplate(doc, channel);
    if (tpl.documentType === doc) return tpl.labelCs;
    return PREP_COMPOSE_LABEL_CS[doc] || `Žádost — ${authorityDocumentTypeLabel(doc)}`;
  }
  return `Žádost — ${authorityDocumentTypeLabel(doc)}`;
}

/** One mustr per importable orgán / síť / povinnost + freeform Jiné. */
export function listAuthorityLetterOptions(
  channel: AuthorityLetterChannel | null | undefined,
): Array<AuthorityLetterTemplate & { documentType: string }> {
  const ch: AuthorityLetterChannel =
    channel === 'email' ? 'email' : channel === 'portal' ? 'portal' : 'isds';
  const catalog = listAuthorityComposeDocumentTypes().map((documentType) => {
    const tpl = pickAuthorityLetterTemplate(documentType, ch);
    return {
      ...tpl,
      documentType,
      labelCs: composeMustrLabelCs(documentType, ch),
    };
  });
  return [
    ...catalog,
    {
      id: `other.${ch}`,
      channel: ch,
      documentType: AUTHORITY_COMPOSE_OTHER_DOCUMENT_TYPE,
      labelCs: 'Jiné',
      subjectTpl: '',
      bodyTpl: '',
    },
  ];
}

/** Labels in letter subjects/bodies — avoid „Žádost o vyjádření — Vyjádření DOSS“. */
export function formatAuthorityLetterDocumentLabel(type: string | null | undefined): string {
  const key = (type ?? '').trim();
  if (key === 'vyjadreni_doss') return 'DOSS';
  if (key === 'vyjadreni_site' || key === 'krizeni_siti') return 'síť';
  return authorityDocumentTypeLabel(type);
}

export function buildAuthorityLetterVars(input: {
  stavba?: string | null;
  kia?: string | null;
  sap?: string | null;
  obec?: string | null;
  contact?: string | null;
  documentType?: string | null;
}): AuthorityLetterTemplateVars {
  return completeLetterVars({
    stavba: dash(input.stavba),
    kia: dash(input.kia),
    sap: dash(input.sap),
    obec: dash(input.obec),
    contact: dash(input.contact),
    documentLabel: formatAuthorityLetterDocumentLabel(input.documentType),
  });
}

export function defaultProposedFilingKeys(): string[] {
  return [...AUTHORITY_FILING_PACKAGE_KEYS];
}

export function proposedFilingLabel(packageKey: string): string {
  if (packageKey === 'land_list') return AUTHORITY_FILING_PACKAGE_LABELS_CS.land_list_ur;
  return (
    AUTHORITY_FILING_PACKAGE_LABELS_CS[packageKey as keyof typeof AUTHORITY_FILING_PACKAGE_LABELS_CS] ||
    packageKey
  );
}

/** Canonical PD / povolení folder for a filing package — folder path, not an invented file. */
const AUTHORITY_FILING_DRIVE_FOLDER_KEY: Record<AuthorityFilingPackageKey, ProjectDriveArtifactFolderKey> = {
  c1_overview: 'pdC1',
  c2_plan: 'pdC2',
  tz: 'pdTz',
  power_of_attorney: 'permitsRoot',
  cover_or_form: 'doss',
  land_list_r100: 'acquisition',
  land_list_met: 'acquisition',
  land_list_ur: 'acquisition',
  subject_specific: 'doss',
};

/** Artifact folder key for a filing package (Drive skeleton), or null if unmapped. */
export function proposedFilingDriveArtifactKey(
  packageKey: string,
): ProjectDriveArtifactFolderKey | null {
  if (packageKey === 'land_list') return 'acquisition';
  const key = AUTHORITY_FILING_DRIVE_FOLDER_KEY[packageKey as AuthorityFilingPackageKey];
  return key ?? null;
}

export function proposedFilingDriveFolderPath(packageKey: string): string | null {
  const folderKey = proposedFilingDriveArtifactKey(packageKey);
  if (!folderKey) return null;
  const skeleton = PROJECT_DRIVE_SKELETON.find((row) => row.key === folderKey);
  return skeleton ? skeleton.segments.join('/') : null;
}

function completeLetterVars(
  vars: Partial<AuthorityLetterTemplateVars> | null | undefined,
): AuthorityLetterTemplateVars {
  const stavba = dash(vars?.stavba);
  const kia = dash(vars?.kia);
  const sap = dash(vars?.sap);
  const obec = dash(vars?.obec);
  return {
    stavba,
    kia,
    sap,
    obec,
    contact: dash(vars?.contact),
    documentLabel: dash(vars?.documentLabel),
    stavbaIdent:
      meaningful(vars?.stavbaIdent) ||
      formatAuthorityStavbaIdent({ stavba, kia, sap, obec }),
  };
}

function dash(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  return raw || '—';
}

function meaningful(value: string | null | undefined): string {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '—') return '';
  return raw;
}
